import { Fragment } from 'react';
import type { FieldDef } from '@/config/masterData';
import { useRows } from '@/hooks/useLive';
import {
  CheckboxInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';

type Values = Record<string, unknown>;

interface EntityFormProps {
  fields: FieldDef[];
  values: Values;
  errors: Record<string, string>;
  onChange: (name: string, value: unknown) => void;
}

/** Renders a form from field descriptors. Sections are declared by the
 *  first field that opens them, so the descriptor stays a flat list. */
export function EntityForm({ fields, values, errors, onChange }: EntityFormProps) {
  return (
    <div className="form-grid">
      {fields.map((f) => (
        <Fragment key={f.name}>
          {f.section && <div className="form-section field--full">{f.section}</div>}
          <div className={f.full || f.type === 'boolean' || f.type === 'textarea' ? 'field--full' : undefined}>
            <FormField field={f} values={values} errors={errors} onChange={onChange} />
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function FormField({
  field: f, values, errors, onChange,
}: { field: FieldDef; values: Values; errors: Record<string, string>; onChange: (n: string, v: unknown) => void }) {
  const id = `f-${f.name}`;
  const error = errors[f.name];
  const raw = values[f.name];

  // Foreign-key options come from the local mirror, so they work offline.
  const lookupRows = useRows(f.lookup?.table ?? '__none__');
  const options =
    f.options ??
    (f.lookup
      ? (lookupRows ?? [])
          .map((r) => {
            const rec = r as Record<string, unknown>;
            return { value: String(rec.id), label: String(rec[f.lookup!.label] ?? rec.code ?? rec.id) };
          })
          .sort((a, b) => a.label.localeCompare(b.label))
      : []);

  switch (f.type) {
    case 'boolean':
      return (
        <CheckboxInput
          id={id}
          label={f.label}
          value={Boolean(raw)}
          help={f.help}
          onChange={(v) => onChange(f.name, v)}
        />
      );

    case 'number':
      return (
        <NumberInput
          id={id}
          label={f.label}
          value={raw === null || raw === undefined || raw === '' ? null : Number(raw)}
          onChange={(v) => onChange(f.name, v)}
          help={f.help}
          error={error}
          required={f.required}
          min={f.min}
          max={f.max}
          step={f.step}
          suffix={f.suffix}
        />
      );

    case 'select':
      return (
        <SelectInput
          id={id}
          label={f.label}
          value={raw == null ? null : String(raw)}
          options={options}
          onChange={(v) => onChange(f.name, v)}
          help={f.help}
          error={error}
          required={f.required}
        />
      );

    case 'textarea':
      return (
        <TextArea
          id={id}
          label={f.label}
          value={raw == null ? '' : String(raw)}
          onChange={(v) => onChange(f.name, v || null)}
          help={f.help}
          error={error}
          required={f.required}
        />
      );

    case 'time':
      return (
        <TextInput
          id={id}
          label={f.label}
          type="time"
          value={raw == null ? '' : String(raw).slice(0, 5)}
          onChange={(v) => onChange(f.name, v || null)}
          help={f.help}
          error={error}
          required={f.required}
        />
      );

    case 'color':
      return (
        <TextInput
          id={id}
          label={f.label}
          type="color"
          value={raw == null ? '#8e8e8e' : String(raw)}
          onChange={(v) => onChange(f.name, v)}
          help={f.help}
          error={error}
        />
      );

    default:
      return (
        <TextInput
          id={id}
          label={f.label}
          value={raw == null ? '' : String(raw)}
          onChange={(v) => onChange(f.name, v || null)}
          help={f.help}
          error={error}
          required={f.required}
        />
      );
  }
}
